import { productVideoMap, setupScrollingText } from './video_map.js?v=2';
import { attachWishlistButton } from './wishlist.js?v=5';
import { test } from './audio_handling.js?';
// Cache name for the browser's Cache API
const CACHE_NAME = 'video-cache-v1';
// Track blob URLs that need to be revoked when they're no longer needed
const blobReferenceCount = {};

/**
 * Fetches the video from the network or cache, converts it to a Blob URL.
 * @param {string} videoURL - The original URL of the video.
 * @returns {Promise<string>} - A promise that resolves to the Blob URL.
 */
async function getCachedBlob(videoURL) {
  try {
    // Try to open the cache
    const cache = await caches.open(CACHE_NAME);

    // Check if we have the video in cache
    let response = await cache.match(videoURL);

    // If not in cache, fetch it and store in cache
    if (!response) {
      console.log(`[getCachedBlob] Fetching video from network for URL: ${videoURL}`);
      response = await fetch(videoURL);

      // Clone the response before putting it in the cache
      const responseToCache = response.clone();
      await cache.put(videoURL, responseToCache);
      console.log(`[getCachedBlob] Added to cache: ${videoURL}`);
    } else {
      console.log(`[getCachedBlob] Found in cache: ${videoURL}`);
    }

    // Convert to blob and create object URL
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    // Initialize reference count
    blobReferenceCount[blobUrl] = (blobReferenceCount[blobUrl] || 0) + 1;

    return blobUrl;
  } catch (error) {
    console.error(`[getCachedBlob] Error fetching video: ${videoURL}`, error);
    throw error;
  }
}

/**
 * Decreases the reference count for a blob URL and revokes it if no longer used
 * @param {string} blobUrl - The blob URL to decrease reference for
 */
function decreaseBlobReference(blobUrl) {
  if (!blobUrl || !blobReferenceCount[blobUrl]) return;

  blobReferenceCount[blobUrl]--;
  if (blobReferenceCount[blobUrl] <= 0) {
    // If reference count reaches zero, revoke the URL to free resources
    URL.revokeObjectURL(blobUrl);
    delete blobReferenceCount[blobUrl];
    console.log(`[decreaseBlobReference] Revoked blob URL: ${blobUrl}`);
  }
}

/**
 * Cleans up abandoned videos that may exist in the card
 * @param {Element} card - The product card element
 * @param {string} handle - The product handle
 */
function cleanupAbandonedVideos(card, handle) {
  const abandonedVideos = card.querySelectorAll('video:not([data-initialized="true"])');
  abandonedVideos.forEach(oldVideo => {
    if (oldVideo.src) {
      decreaseBlobReference(oldVideo.getAttribute('data-blob-url'));
    }
    oldVideo.remove();
    console.log(`[cleanupAbandonedVideos] Removed abandoned video for handle: ${handle}`);
  });
}

/**
 * Sets up the video element with all necessary attributes
 * @param {HTMLVideoElement} video - The video element to set up
 */
function setupVideoElement(video) {
  video.className = 'product-card__video';
  video.loop = true;
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.style.width = '100%';
  video.style.height = 'auto';
  video.style.display = 'none';
  video.style.objectFit = 'cover';
  video.setAttribute('loading', 'lazy');
  video.setAttribute('data-initialized', 'true');
}

/**
 * Sets up event listeners for the video element
 * @param {HTMLVideoElement} video - The video element
 * @param {Element} primaryImage - The primary image element
 * @param {Element} card - The product card element
 * @param {string} handle - The product handle
 */
function setupVideoEvents(video, primaryImage, card, handle) {
  video.addEventListener('loadedmetadata', () => {
    console.log(`[createVideoForCard] loadedmetadata for handle: ${handle}`);
    primaryImage.style.display = 'none';
    video.style.display = 'block';

    // Style product info bar (if exists)
    const info = card.querySelector('.product-card__info');
    if (info) {
      info.style.position = 'absolute';
      info.style.bottom = 0;
      info.style.background = '#ffffff30';
    }
    setupScrollingText(card);
  }, { once: true });

  video.addEventListener('error', (e) => {
    console.error(`[createVideoForCard] Error loading video for handle: ${handle}`, e);
  }, { once: true });
}

/**
 * Track which videos are currently being created
 */
const videoCreating = {};

/**
 * Asynchronously creates a video element for a product card, using a cached Blob URL.
 * @param {Element} card - The product card element.
 * @returns {Promise<HTMLVideoElement|null>} - Resolves with the created (or reused) video element.
 */
async function createVideoForCard(card) {
  const figure = card.querySelector('.product-card__figure');
  if (!figure) {
    console.log('[createVideoForCard] No figure element in card; skipping video creation.');
    return null;
  }

  const anchor = figure.querySelector('a');
  const primaryImage = figure.querySelector('.product-card__image--primary');
  if (!anchor || !primaryImage) {
    console.log('[createVideoForCard] Missing anchor or primary image; skipping video creation.');
    return null;
  }

  // Extract the product handle from the anchor's href
  const urlParts = anchor.href.split('/');
  const handle = urlParts[urlParts.length - 1].split('?')[0];
  console.log(`[createVideoForCard] Called for handle: ${handle}`);

  // Reuse existing video if already created
  const existingVideo = card.querySelector('video[data-initialized="true"]');
  if (existingVideo) {
    console.log(`[createVideoForCard] Video already exists for handle: ${handle}`);
    return existingVideo;
  }

  const videoURL = productVideoMap[handle];
  if (!videoURL) {
    console.log(`[createVideoForCard] No video URL for handle: ${handle} in productVideoMap.`);
    return null;
  }

  // Create the video element
  console.log(`creating element for ${handle}`);
  if (handle in videoCreating) {
    console.log("race condition was about to hit");
    return null;
  }
  videoCreating[handle] = true;

  // Clean up any previously abandoned videos for this card
  cleanupAbandonedVideos(card, handle);

  const video = document.createElement('video');
  setupVideoElement(video);

  // Fetch the video as a blob and set as video source
  try {
    const cachedBlobUrl = await getCachedBlob(videoURL);
    video.src = cachedBlobUrl;
    video.setAttribute('data-blob-url', cachedBlobUrl);
    console.log(`[createVideoForCard] Set video.src for handle: ${handle} using blob URL: ${cachedBlobUrl}`);
  } catch (error) {
    console.error(`[createVideoForCard] Failed to get cached blob for handle: ${handle}`, error);
    delete videoCreating[handle];
    return null;
  }

  // Insert the video into the DOM right after the primary image
  anchor.insertBefore(video, primaryImage.nextSibling);

  // Set up event listeners
  setupVideoEvents(video, primaryImage, card, handle);

  console.log(`[createVideoForCard] Video creation complete for handle: ${handle}`);
  delete videoCreating[handle];
  attachWishlistButton(card, handle);
  return video;
}

/**
 * Plays the video if it exists and is paused
 * @param {HTMLVideoElement} video - The video element
 * @param {string} handle - The product handle
 */
function playVideoIfExists(video, handle) {
  if (video && video.paused) {
    console.log(`[Observer] Attempting to play video for handle: ${handle}.`);
    video.play().catch(e => {
      console.warn(`[Observer] Play failed for handle: ${handle}`, e);
    });
  }
}

const videoLoadQueue = [];
let isLoadingVideo = false;

  function handleCardVisible(card, handle) {
  videoLoadQueue.push({ card, handle });
  processVideoQueue();
}
async function processVideoQueue() {
  if (isLoadingVideo || videoLoadQueue.length === 0) return;

  isLoadingVideo = true;
  const { card, handle } = videoLoadQueue.shift();

  const existingVideo = card.querySelector('video[data-initialized="true"]');
  if (existingVideo) {
    playVideoIfExists(existingVideo, handle);
    isLoadingVideo = false;
    processVideoQueue(); // move to next in queue
    return;
  }

  const video = await createVideoForCard(card);
  playVideoIfExists(video, handle);

  isLoadingVideo = false;
  processVideoQueue(); // continue to next
}

/**
 * Handles when a card is hidden from the viewport
 * @param {Element} card - The product card element
 * @param {string} handle - The product handle
 */
function handleCardHidden(card, handle) {
  const video = card.querySelector('video[data-initialized="true"]');
  if (video && !video.paused) {
    console.log(`[Observer] Pausing video for handle: ${handle}.`);
    video.pause();
  }
}

/**
 * Handles intersection observer entries
 * @param {IntersectionObserverEntry[]} entries - The intersection observer entries
 */
function handleIntersection(entries) {
  entries.forEach(entry => {
    const card = entry.target;
    const figure = card.querySelector('.product-card__figure');
    const anchor = figure ? figure.querySelector('a') : null;
    const urlParts = anchor ? anchor.href.split('/') : [];
    const handle = urlParts.length ? urlParts[urlParts.length - 1].split('?')[0] : 'unknown-handle';
    console.log(`[Observer] Card for handle: ${handle} isIntersecting: ${entry.isIntersecting}`);

    if (entry.isIntersecting) {
      handleCardVisible(card, handle);
    } else {
      handleCardHidden(card, handle);
    }
  });
}

/**
 * Creates a new IntersectionObserver
 * @returns {IntersectionObserver} - The created observer
 */
function newIntersectionObserver() {
  return new IntersectionObserver(handleIntersection, { threshold: 0.1 });
}

/**
 * Sets up an IntersectionObserver to monitor product cards,
 * triggering video creation and play/pause actions based on viewport visibility.
 * @param {Element[]} productCards - Array of product-card elements.
 */
function setupObserver(productCards) {
  if (!('IntersectionObserver' in window)) {
    console.warn('IntersectionObserver not supported; videos will not be auto-handled.');
    return;
  }

  const observer = newIntersectionObserver();
  productCards.forEach(card => observer.observe(card));
}

/**
 * Cleans up a video element and revokes its blob URL
 * @param {HTMLVideoElement} video - The video element to clean up
 */
function cleanupVideo(video) {
  if (video && video.getAttribute('data-blob-url')) {
    const blobUrl = video.getAttribute('data-blob-url');
    decreaseBlobReference(blobUrl);
    video.removeAttribute('data-blob-url');
    video.src = '';
  }
}

/**
 * Cleans up old cache entries to prevent unlimited growth
 */
async function cleanupOldCacheEntries() {
  try {
    const cache = await caches.open(CACHE_NAME);
    const requests = await cache.keys();

    // If we have more than 50 videos cached, remove the oldest ones
    if (requests.length > 500) {
      const oldestRequests = requests.slice(0, requests.length - 50);
      await Promise.all(oldestRequests.map(request => cache.delete(request)));
      console.log(`[cleanupOldCacheEntries] Removed ${oldestRequests.length} old cache entries`);
    }
  } catch (error) {
    console.error('[cleanupOldCacheEntries] Error cleaning cache', error);
  }
}


/**
 * Entry point: finds all product-card elements, sets up the observer, and initializes video handling.
 */
function initializeVideoCards() {
  let filterButton = document.querySelector("facet-floating-filter.facets__floating-filter");
  if (filterButton != null){
    filterButton.remove();
  }
  console.log("[initializeVideoCards] Initialization started.");


  // Clean up old cache entries
  cleanupOldCacheEntries();

  const productCards = Array.from(document.querySelectorAll('product-card'));
  console.log(`[initializeVideoCards] Found ${productCards.length} product cards.`);
  setupObserver(productCards);
  console.log("[initializeVideoCards] Observer setup complete.");

  // Set up a cleanup function for when the page is unloaded
  window.addEventListener('beforeunload', () => {
    productCards.forEach(card => {
      const video = card.querySelector('video[data-initialized="true"]');
      if (video) {
        cleanupVideo(video);
      }
    });
  });
}
function observeNewProductCards() {
  const productGrid = document.querySelector('.product-list');
  if (!productGrid) return;

  const mutationObserver = new MutationObserver(mutations => {
    const newCards = [];

    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (!(node instanceof HTMLElement)) return;

        if (node.matches?.('.product-card')) {
          newCards.push(node);
        } else {
          const foundCards = node.querySelectorAll?.('.product-card');
          if (foundCards?.length) {
            newCards.push(...foundCards);
          }
        }
      });
    });

    if (newCards.length > 0) {
      console.log(`[observeNewProductCards] Found ${newCards.length} new product cards.`);
      setupObserver(newCards);
    }
  });

  mutationObserver.observe(productGrid, {
    childList: true,
    subtree: false // set to true only if nested product-cards are added
  });
}
document.addEventListener('DOMContentLoaded', () => {
  initializeVideoCards();
  observeNewProductCards();
});
